const NODES = [
  {
    key: "n-emp",
    icon: "bi-person-badge",
    label: "Employees",
    iconColor: "#2DD4BF",
    pos: "top-[2%] left-1/2 -translate-x-1/2",
  },
  {
    key: "n-ven",
    icon: "bi-building",
    label: "Vendors",
    iconColor: "#60A5FA",
    pos: "top-[28%] right-0",
    gold: true,
  },
  {
    key: "n-par",
    icon: "bi-handshake",
    label: "Partners",
    iconColor: "#C4B5FD",
    pos: "bottom-[22%] right-[4%]",
  },
  {
    key: "n-mail",
    icon: "bi-envelope-fill",
    label: "Email",
    iconColor: "#93C5FD",
    pos: "bottom-[2%] left-1/2 -translate-x-1/2",
  },
  {
    key: "n-wa",
    icon: "bi-whatsapp",
    label: "WhatsApp",
    iconColor: "#4ADE80",
    pos: "bottom-[22%] left-[2%]",
  },
  {
    key: "n-var",
    icon: "bi-diagram-3",
    label: "Variance",
    iconColor: "#FBBF24",
    pos: "top-[28%] left-0",
    gold: true,
  },
];

export default function ConsentHero() {
  return (
    <header className="relative bg-ink text-white py-[4.5rem] pb-20 overflow-hidden min-h-[min(88vh,720px)] flex items-center">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 50% at 85% 40%, rgba(11, 143, 122, 0.28), transparent 55%), radial-gradient(ellipse 40% 35% at 10% 80%, rgba(201, 164, 92, 0.12), transparent 50%)",
        }}
      />
      <div className="container mx-auto max-w-[1140px] px-4 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 xl:gap-12 items-center">
          <div>
            <p className="inline-block text-[0.78rem] font-semibold tracking-[0.12em] uppercase text-teal-bright mb-[0.85rem]">
              Consent Management
            </p>
            <h1 className="font-bricolage text-[clamp(2.1rem,4.2vw,3rem)] mb-4 max-w-[14ch] text-white">
              One place for every consent you collect.
            </h1>
            <p className="text-[1.05rem] leading-[1.65] text-white/[.62] max-w-[32rem] mb-7">
              Capture, track and prove consent for employees, vendors and
              partners — linked to Data Discovery so you always know what was
              consented versus what data actually sits in your systems.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="#contact"
                className="bg-teal hover:bg-teal-bright text-white font-semibold rounded px-8 py-3 text-lg transition-all hover:-translate-y-px"
              >
                Request a demo
              </a>
              <a
                href="#audiences"
                className="bg-transparent text-white/90 border border-white/[.28] hover:border-white hover:text-white hover:bg-white/[.06] font-semibold rounded px-8 py-3 text-lg transition-all"
              >
                See capabilities
              </a>
            </div>
          </div>

          <div>
            <div className="relative w-full max-w-[480px] aspect-square mx-auto mt-10 lg:mt-0">
              {/* glow */}
              <div
                className="absolute inset-[18%] rounded-full blur-[6px] animate-glow-breathe motion-safe-anim"
                style={{
                  background:
                    "radial-gradient(circle, rgba(18, 184, 154, 0.28), transparent 68%)",
                }}
              />
              {/* rings */}
              <div
                className="absolute inset-[6%] rounded-full border border-dashed animate-spin-slow motion-safe-anim"
                style={{
                  borderColor: "rgba(18, 184, 154, 0.28)",
                  animationDuration: "50s",
                }}
              />
              <div
                className="absolute inset-[16%] rounded-full border animate-spin-rev motion-safe-anim"
                style={{
                  borderColor: "rgba(201, 164, 92, 0.22)",
                  animationDuration: "40s",
                }}
              />

              {/* SVG spokes */}
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 480 480"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient
                    id="cLine"
                    x1="80"
                    y1="80"
                    x2="400"
                    y2="400"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#12B89A" stopOpacity="0.7" />
                    <stop offset="0.5" stopColor="#C9A45C" stopOpacity="0.35" />
                    <stop offset="1" stopColor="#60A5FA" stopOpacity="0.55" />
                  </linearGradient>
                </defs>
                <line
                  x1="240"
                  y1="240"
                  x2="240"
                  y2="55"
                  stroke="url(#cLine)"
                  strokeWidth="1.4"
                  strokeDasharray="4 5"
                />
                <line
                  x1="240"
                  y1="240"
                  x2="410"
                  y2="160"
                  stroke="url(#cLine)"
                  strokeWidth="1.4"
                  strokeDasharray="4 5"
                />
                <line
                  x1="240"
                  y1="240"
                  x2="395"
                  y2="350"
                  stroke="url(#cLine)"
                  strokeWidth="1.4"
                  strokeDasharray="4 5"
                />
                <line
                  x1="240"
                  y1="240"
                  x2="240"
                  y2="430"
                  stroke="url(#cLine)"
                  strokeWidth="1.4"
                  strokeDasharray="4 5"
                />
                <line
                  x1="240"
                  y1="240"
                  x2="85"
                  y2="350"
                  stroke="url(#cLine)"
                  strokeWidth="1.4"
                  strokeDasharray="4 5"
                />
                <line
                  x1="240"
                  y1="240"
                  x2="70"
                  y2="160"
                  stroke="url(#cLine)"
                  strokeWidth="1.4"
                  strokeDasharray="4 5"
                />
                <path
                  d="M85 160 Q120 240 85 350"
                  stroke="rgba(18,184,154,0.2)"
                  strokeWidth="1"
                  fill="none"
                />
                <path
                  d="M395 160 Q360 240 395 350"
                  stroke="rgba(201,164,92,0.2)"
                  strokeWidth="1"
                  fill="none"
                />
                <circle cx="240" cy="55" r="3.5" fill="#2DD4BF" />
                <circle cx="410" cy="160" r="3.5" fill="#60A5FA" />
                <circle cx="395" cy="350" r="3.5" fill="#C4B5FD" />
                <circle cx="240" cy="430" r="3.5" fill="#93C5FD" />
                <circle cx="85" cy="350" r="3.5" fill="#4ADE80" />
                <circle cx="70" cy="160" r="3.5" fill="#FBBF24" />
              </svg>

              {/* hub */}
              <div
                className="absolute w-[132px] h-[132px] rounded-full flex flex-col items-center justify-center text-center z-[3] animate-logo-float motion-safe-anim"
                style={{
                  top: "50%",
                  left: "50%",
                  marginTop: "-66px",
                  marginLeft: "-66px",
                  background:
                    "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.1), transparent 55%), linear-gradient(160deg, #123248 0%, #0A1628 100%)",
                  border: "2px solid rgba(18, 184, 154, 0.55)",
                  boxShadow:
                    "0 0 0 10px rgba(7, 17, 31, 0.45), 0 0 50px rgba(18, 184, 154, 0.25), 0 20px 40px rgba(0,0,0,0.35)",
                }}
              >
                <i className="bi bi-toggle2-on text-[1.65rem] text-teal-bright mb-[0.35rem]" />
                <strong className="font-bricolage text-[0.92rem] font-semibold leading-[1.15]">
                  Consent
                  <br />
                  Hub
                </strong>
                <span className="text-[0.62rem] tracking-[0.08em] uppercase text-white/45 mt-1">
                  OneDPDP
                </span>
              </div>

              {/* orbit nodes */}
              {NODES.map((n) => (
                <div
                  key={n.key}
                  className={`absolute z-[2] min-w-[104px] px-3 py-[0.65rem] rounded-[10px] bg-[rgba(14,26,43,0.92)] border backdrop-blur-[8px] text-center shadow-[0_10px_28px_rgba(0,0,0,0.28)] ${n.pos}`}
                  style={{
                    borderColor: n.gold
                      ? "rgba(201, 164, 92, 0.25)"
                      : "rgba(255,255,255,0.12)",
                  }}
                >
                  <i
                    className={`bi ${n.icon} block text-[1.15rem] mb-1`}
                    style={{ color: n.iconColor }}
                  />
                  <strong className="block text-[0.78rem] font-semibold">
                    {n.label}
                  </strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
